import React from 'react';
import CaseStudyCard from './CaseStudyCard';
import { PASSWORD_PROTECTED as SCHRODINGER_GATED } from '../../pages/Schrodinger';
import '../../styles/components/CaseStudies.css';

import BallysLogo from '../assets/Logos/Ballys.png';
import SchrodingerLogo from '../assets/Logos/Schrodinger.png';
import OurDateLogo from '../assets/Logos/OurDate.svg';
import JerryLogo from '../assets/Logos/Jerry.svg';
import RRMSLogo from '../assets/Logos/RRMS.png';
import LassiLogo from '../assets/Logos/LassiAndSpice.svg';
import TraviaLogo from '../assets/Logos/Travia.png';
import MultiBoxLogo from '../assets/Logos/MultiBox.svg';

/**
 * Case studies, split into two groups. Each card uses the unified
 * <CaseStudyCard> treatment: grainy pastel bg + brand logo + metadata.
 *
 * Pastel palette: low saturation, similar lightness across all cards
 * so they cohere as a set instead of competing.
 *
 *   internal? — true uses <Link> (routes inside the SPA)
 *               false uses <a target="_blank"> (external Behance, etc.)
 *
 * Professional work is deliberately the FIRST group and is meant to
 * stay that way. New work goes into `selfInitiated` below, where it
 * can never push the employment history down the page — that is the
 * reason this is two groups rather than one list sorted by date.
 *
 * Order within each group is newest first. Not sorted by pageviews:
 * the two Behance cards are external links that fire no analytics
 * event, so their zero is "unmeasured", not "unpopular", and ranking
 * on views would quietly bury them for good.
 */
const GROUPS = [
  {
    id: 'professional',
    title: 'Professional work',
    meta: '2022 — 2026',
    items: [
      {
        title: "Bally's Interactive",
        role: 'Full-time · Product Designer',
        period: '2025 — 2026',
        logo: BallysLogo,
        bgColor: '#FAEDED',
        to: '/ballys',
      },
      {
        title: 'Schrödinger',
        role: 'Full-time · UX Designer',
        period: '2023 — 2025',
        logo: SchrodingerLogo,
        bgColor: '#EBF3FA',
        to: '/schrodinger',
        // Follows the gate itself, so the badge can never claim the
        // page is locked when it isn't. Flip PASSWORD_PROTECTED in
        // pages/Schrodinger.js to restore both at once.
        passwordProtected: SCHRODINGER_GATED,
      },
      {
        title: 'OurDate',
        role: 'Internship · UX Designer',
        period: '2022',
        logo: OurDateLogo,
        bgColor: '#EBF6F5',
        to: '/ourdate',
      },
    ],
  },
  {
    id: 'self-initiated',
    title: 'Self-initiated projects',
    meta: null,
    items: [
      {
        // CaseStudyCard renders role and period only — `title` feeds the
        // logo alt, never visible text. The other cards don't need it:
        // their brand logo is the name. This one's mark is generic, so
        // the role line carries the subject as well as the artefact type.
        // "PRD" rather than "Case study" is deliberate — every other card
        // in this group reads "Case study · …", and breaking that pattern
        // is what flags this as product-management work.
        title: 'Multi-box supply chain',
        role: 'Concept PRD · Multi-box supply chain',
        period: '2026',
        logo: MultiBoxLogo,
        bgColor: '#F4EDE3',
        to: '/multi-box',
      },
      {
        title: 'Jerry',
        role: 'Case study · Research aide platform',
        period: '2023',
        logo: JerryLogo,
        bgColor: '#EBF1F1',
        to: '/jerry',
      },
      {
        title: 'RapidSMS',
        role: 'Case study · Alarm-monitoring redesign',
        period: '2023',
        logo: RRMSLogo,
        bgColor: '#ECF1F8',
        to: '/rrms',
        whiteBg: true,
      },
      {
        title: 'Lassi & Spice',
        role: 'Case study · Restaurant brand redesign',
        period: '2022',
        logo: LassiLogo,
        bgColor: '#FAF1E5',
        href: 'https://www.behance.net/gallery/162215037/Lassi-Spice-A-Redesign-project',
      },
      {
        title: 'Travia',
        role: 'Case study · Travel application',
        period: '2021',
        logo: TraviaLogo,
        bgColor: '#F1F1F4',
        href: 'https://www.behance.net/gallery/159697011/TRAVIA-A-Travel-Application-Case-Study',
      },
    ],
  },
];

const CaseStudies = () => {
  return (
    <div className="tab-content case-studies-tab">
      {GROUPS.map((group) => (
        <section
          className="case-studies-group"
          key={group.id}
          aria-labelledby={`case-studies-${group.id}`}
        >
          <div className="case-studies-group__heading">
            <h3 className="case-studies-group__title" id={`case-studies-${group.id}`}>
              {group.title}
            </h3>
            {group.meta && <span className="case-studies-group__meta">{group.meta}</span>}
            <span className="case-studies-group__rule" aria-hidden="true" />
          </div>
          <div className="case-studies-grid">
            {group.items.map((cs) => (
              <CaseStudyCard key={cs.title} {...cs} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default CaseStudies;
